import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, cartItemPrice } from "@/api";

const StoreContext = createContext(null);

export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fr_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem("fr_admin_token") || "");

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await http.get("/settings");
      setSettings(data);
    } catch (e) {
      console.error("Settings load failed", e);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    http.get("/products").then(({ data }) => {
      const byId = Object.fromEntries(data.map((p) => [p.id, p]));
      setCart((prev) =>
        prev
          .filter((i) => byId[i.product_id])
          .map((i) => {
            const p = byId[i.product_id];
            return { ...i, key: i.key || i.product_id, retail_price: p.retail_price, promo_price: p.promo_price, wholesale_tiers: p.wholesale_tiers || [], variants: p.variants || [] };
          }),
      );
    }).catch(() => {});
  }, [loadSettings]);

  useEffect(() => {
    localStorage.setItem("fr_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (!settings?.brand_colors) return;
    const root = document.documentElement;
    Object.entries(settings.brand_colors).forEach(([k, v]) => {
      root.style.setProperty(`--brand-${k}`, v);
    });
  }, [settings]);

  function addToCart(product, qty = 1, opts = {}) {
    const key = [product.id, opts.variant || "", opts.color || ""].join("|");
    setCart((prev) => {
      if (prev.some((i) => (i.key || i.product_id) === key)) {
        return prev.map((i) => ((i.key || i.product_id) === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          code: product.code,
          name: product.name,
          image: product.images?.[0] || "",
          retail_price: product.retail_price,
          promo_price: product.promo_price,
          wholesale_tiers: product.wholesale_tiers || [],
          variants: product.variants || [],
          variant: opts.variant || null,
          color: opts.color || null,
          qty,
        },
      ];
    });
    setCartOpen(true);
  }

  function updateQty(key, qty) {
    setCart((prev) => prev.map((i) => ((i.key || i.product_id) === key ? { ...i, qty: Math.max(1, qty) } : i)));
  }

  function removeFromCart(key) {
    setCart((prev) => prev.filter((i) => (i.key || i.product_id) !== key));
  }

  function clearCart() {
    setCart([]);
  }

  const cartTotals = cart.reduce(
    (acc, i) => {
      acc.subtotal += cartItemPrice(i) * i.qty;
      acc.items += i.qty;
      return acc;
    },
    { subtotal: 0, items: 0 },
  );

  function setToken(t) {
    if (t) {
      localStorage.setItem("fr_admin_token", t);
    } else {
      localStorage.removeItem("fr_admin_token");
    }
    setAdminToken(t || "");
  }

  return (
    <StoreContext.Provider
      value={{
        settings,
        reloadSettings: loadSettings,
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        cartTotals,
        adminToken,
        setToken,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}
